import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil } from "lucide-react";
import { api, formatApiError, brl } from "@/lib/api";
import { ContextMenu } from "@/components/ContextMenu";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function Servicos() {
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", price: "" });
  const [menu, setMenu] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchServices = () => api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  useEffect(() => { fetchServices(); }, []);

  const filtered = services.filter((s) => norm(s.name).includes(norm(search)));

  const startEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, price: String(s.price) });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do serviço");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/services/${editing.id}`, { name: form.name, price: Number(form.price) || 0 });
        toast.success("Serviço atualizado");
      } else {
        await api.post("/services", { name: form.name, price: Number(form.price) || 0 });
        toast.success("Serviço cadastrado");
      }
      fetchServices();
      setEditing(null);
      setForm({ name: "", price: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Excluir o serviço "${s.name}"?`)) return;
    try {
      await api.delete(`/services/${s.id}`);
      toast.success("Serviço excluído");
      if (editing?.id === s.id) {
        setEditing(null);
        setForm({ name: "", price: "" });
      }
      fetchServices();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const field = "w-full rounded-lg border border-stone-300 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all";

  return (
    <div data-testid="servicos-page">
      <h1 className="font-display text-3xl sm:text-4xl font-light mb-6">Serviços</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)] flex flex-col max-h-[75vh]">
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar serviço…"
              className="w-full rounded-full border border-stone-300 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] transition-all"
              data-testid="servicos-search-input"
            />
          </div>
          <div className="flex-1 overflow-y-auto thin-scroll space-y-2">
            {filtered.length === 0 && <p className="text-sm text-stone-400 py-6 text-center">Nenhum serviço encontrado.</p>}
            {filtered.map((s) => (
              <div
                key={s.id}
                onClick={() => startEdit(s)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, service: s });
                }}
                className={`flex items-center justify-between px-4 py-3.5 rounded-xl border cursor-pointer transition-colors ${
                  editing?.id === s.id ? "bg-[#F3EFE6] border-[#C8A279]" : "border-stone-100 hover:border-stone-300"
                }`}
                data-testid={`servico-row-${s.id}`}
                title="Clique direito para alterar ou excluir"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Pencil size={13} className="text-stone-300 shrink-0" />
                  <p className="text-sm font-medium truncate">{s.name}</p>
                </div>
                <p className="text-sm font-semibold" data-testid={`servico-price-${s.id}`}>{brl(s.price)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] h-fit">
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8A279] mb-1">
            {editing ? "Alterar serviço" : "Cadastro"}
          </p>
          <h2 className="font-display text-2xl mb-5" data-testid="servico-form-title">{editing ? editing.name : "Novo serviço"}</h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Nome do serviço</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                list="servicos-names"
                className={`mt-1.5 ${field}`}
                data-testid="servico-name-input"
                placeholder="Ex.: Corte Masculino"
              />
              <datalist id="servicos-names">
                {services.map((s) => (
                  <option key={s.id} value={s.name} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Preço de venda (R$)</label>
              <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={`mt-1.5 ${field}`} data-testid="servico-price-input" placeholder="0,00" />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={save} disabled={busy} className="px-7 py-3 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-50" data-testid="servico-save-button">
                {editing ? "Salvar alterações" : "Cadastrar"}
              </button>
              {editing && (
                <button onClick={() => { setEditing(null); setForm({ name: "", price: "" }); }} className="px-6 py-3 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors" data-testid="servico-clear-button">
                  Limpar
                </button>
              )}
            </div>
          </div>
          {!editing && (
            <button
              onClick={() => { setEditing(null); setForm({ name: "", price: "" }); }}
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#B38F68] hover:text-[#121212] transition-colors"
              data-testid="servico-new-button"
            >
              <Plus size={15} /> Cadastrar novo serviço
            </button>
          )}
        </div>
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            { label: "Alterar", testId: "alterar", action: () => startEdit(menu.service) },
            { label: "Excluir", testId: "excluir", danger: true, action: () => remove(menu.service) },
          ]}
        />
      )}
    </div>
  );
}
